import React from "react";
import dynamic from "next/dynamic";
import PropTypes from "prop-types";
import CustomLink from "@/bit/components/components.custom-link";
import Button from "@/bit/components/components.button";
import CardContent from "./card-content";

import { IMAGE_TYPE } from "./enums.js";
const TextInfo = dynamic(() => import("@/bit/components/components.text"));

export { IMAGE_TYPE };

const DETAIL_CARD_TAGGING_INFO = {
  componentName: "DetailCard",
  componentVariation: "",
  componentContent: "Modal Ficha",
  eventType: "click",
};

const DETAIL_CARD_CONTENT_TAGGING_INFO = {
  componentName: "DetailCard",
  componentVariation: "all",
};

/**
 * Componente que permite crear cards de ofertas
 */
function DetailCard({
  detailCardProps,
  cmsHandler,
  index,
  blockName,
  alias,
  frontalListToDelete,
  CmsEditableGroupIcons,
  contentLoaded = true,
}) {
  const {
    "cms-atom-link-1": detailCardLink,
    "cms-hiring-details-1": hiringDetails,
    "cms-atom-button-1": detailCardButton1,
    "cms-atom-button-2": detailCardButton2,
    hide,
    inherited,
    layout = "default",
    creativityId = "",
    creativityName = "",
  } = detailCardProps;

  const cardHasLink =
    detailCardLink && (detailCardLink?.href || detailCardLink?.onClick);

  const cardButtonsHaveLink =
    detailCardButton1?.href ||
    detailCardButton1?.onClick ||
    detailCardButton2?.href ||
    detailCardButton2?.onClick;

  const isCardLinkSameAsButton2Link =
    cardHasLink && detailCardLink?.href === detailCardButton2?.href;

  if (detailCardLink && !detailCardLink.taggingInfo) {
    detailCardLink.taggingInfo = {
      ...DETAIL_CARD_TAGGING_INFO,
    };
  }
  if (detailCardLink && detailCardLink.taggingInfo) {
    detailCardLink.taggingInfo = {
      ...DETAIL_CARD_TAGGING_INFO,
      ...detailCardLink.taggingInfo,
    };
  }

  let taggingButton1 = {
    ...DETAIL_CARD_CONTENT_TAGGING_INFO,
    componentContent: detailCardButton1?.label || "Lo quiero",
    ...detailCardButton1?.taggingInfo,
  };

  let taggingButton2 = {
    ...DETAIL_CARD_CONTENT_TAGGING_INFO,
    componentContent: detailCardButton2?.label || "Ver detalle",
    ...detailCardButton2?.taggingInfo,
  };

  // LÓGICA PARA CARDS DE PAC
  if (
    layout === "offer" &&
    ((!detailCardProps.originalPrice &&
      !detailCardProps.librePrice &&
      !detailCardProps.currentPrice) ||
      !detailCardProps["cms-atom-link-1"]?.href)
  ) {
    return null;
  } else if (
    layout === "offer" &&
    detailCardProps["cms-atom-price-2"]?.price < 0 &&
    detailCardProps["cms-atom-price-2"]?.alternativeAmount &&
    detailCardProps.alternativeMonthlyPayment
  ) {
    detailCardProps["cms-atom-price-2"].price =
      detailCardProps["cms-atom-price-2"].alternativeAmount;
  }

  // LOGICA ENTORNO PRODUCTIVO
  let DetailCardTemplate = cardHasLink ? (
    <>
      <CustomLink
        {...detailCardLink}
        className="block-link"
        cydata="DetailCard CustomLink"
      >
        <CardContent detailCardProps={detailCardProps} />
      </CustomLink>
      <CustomLink {...detailCardLink} taggingInfo={{}} tabIndex="1" />
    </>
  ) : (
    <CardContent detailCardProps={detailCardProps} />
  );

  // LOGICA ENTORNO CMS
  if (cmsHandler && !inherited) {
    DetailCardTemplate = (
      <>
        {/* Bolitas de edicion aqui */}
        {CmsEditableGroupIcons && (
          <CmsEditableGroupIcons
            elementProps={{
              ...detailCardProps,
              hiringDetails,
              editName: "cms-block.cms-detail-card",
            }}
            deleteProps={{
              index,
              itemIdDotNotation: `${blockName}.cms-ml-detail-card-1`,
              alias,
              frontalListToDelete,
            }}
            visibleTools={{
              hiring: true,
              edit: true,
              deleteItem: true,
            }}
            cmsHandler={cmsHandler}
          />
        )}
        <CardContent
          detailCardProps={detailCardProps}
          cmsHandler={cmsHandler}
        />
        <CustomLink
          {...detailCardLink}
          cydata="DetailCard CustomLink"
          cmsHandler={cmsHandler}
        />
      </>
    );
  }

  // LOGICA CARDS HEREDADAS
  if (cmsHandler && inherited) {
    DetailCardTemplate = (
      <>
        {/* Cartel Origen Card al heredar */}
        <div className="inherited-text">
          <TextInfo
            color="white"
            align="center"
            size="t4"
            text={`Esta card pertenece a la ficha ${inherited}. Para cambiar su contenido dirígase a la página correspondiente.`}
          />
        </div>
        <CardContent detailCardProps={{ ...detailCardProps }} />
      </>
    );
  }

  // LOGICA CARDS DISTRI POR CUSTOMIZER
  if (cmsHandler && creativityId && creativityName) {
    DetailCardTemplate = (
      <>
        {/* Cartel Origen Card al heredar */}
        <div className="inherited-text">
          <TextInfo
            color="white"
            align="center"
            size="t4"
            text={`Esta card pertenece a la creatividad ${creativityName}. Para cambiar su contenido dirígase <a target="_blank" href="https://admin.customizer.es/#/cmsCreativities/edit/undefined/${creativityId}">aquí</a>.`}
          />
        </div>
        <CardContent detailCardProps={{ ...detailCardProps }} />
      </>
    );
  }

  const currentClassName = `DetailCard${hide ? " hide" : ""}`;

  return (
    <div className={currentClassName}>
      <div
        className={`card-content layout--${layout}${
          cmsHandler && inherited ? " inherited-card" : ""
        }`}
      >
        {DetailCardTemplate}
        {/* BOTONES */}
        {(cardButtonsHaveLink || cmsHandler) && (
          <div className="card-action-buttons">
            <Button
              {...detailCardButton1}
              taggingInfo={taggingButton1}
              cmsHandler={cmsHandler}
            />
            <Button
              {...detailCardButton2}
              taggingInfo={taggingButton2}
              cmsHandler={cmsHandler}
              tabIndex={isCardLinkSameAsButton2Link ? "-1" : "0"} // Navegable por tabulacion si no coincide con link de la card
            />
          </div>
        )}
        <div className={contentLoaded ? "skeleton loaded" : "skeleton"} />
      </div>
      <style jsx>{`.DetailCard {
  position: relative;
  height: 100%;
  &.hide {
    display: none;
  }
  :global(.inherited-text) {
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(88, 88, 88, 0.7); /* Velo oscuro semitransparente */
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    border-radius: 8px;
    backdrop-filter: blur(4px); /* Difumina el fondo detrás del velo */
  }
  .edit-component {
    border-radius: 16px;
    background: rgba(0, 0, 0, 0.8);
    right: 40px;
    width: 32px;
  }

  .card-content {
    display: flex;
    position: relative;
    flex-direction: column;
    background-color: #fff;
    min-width: 316px;
    width: min(20.9vw, 352px);
    height: 100%;
    border-radius: 16px;
    overflow: hidden;
    border: 1px solid #dddddd;

    &.inherited-card {
      filter: grayscale(50%) brightness(0.8);
    }

    // Caso de cards pequeñas com la venta cruzada
    &.layout--small {
      min-width: 240px;
      width: min(20.9vw, 240px);
      padding: 1rem;
      gap: 4px;
      .image-gap {
        min-height: unset;
        aspect-ratio: 4 / 5;
        // 414∶269
      }
      .content {
        padding: 0;
      }
    }

    @media screen and (max-width: 1024px) {
      width: 20rem;
      &.layout--small {
        min-width: 190px;
        width: min(20.9vw, 190px);
      }
    }
  }

  .card-action-buttons {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    z-index: 2;
    margin-top: auto;
    padding: 1rem;
    padding-top: 0;
    margin-top: 8px;

    :global(.buttonx) {
      position: relative !important;
    }
  }

  .skeleton {
    position: absolute;
    display: block;
    top: 0;
    left: 0;
    &.loaded {
      display: none;
    }
  }
}
`}</style>
    </div>
  );
}

DetailCard.propTypes = {
  /**
   * {
   *  "info": "Tamaño imagen card",
   *  "kind": "both",
   *  "beautifulName": "ImageSize",
   *  "values": "./enums.js:IMAGE_TYPE"
   * }
   */
  imageSize: PropTypes.string,
  /**
   * {
   *  "info": "Velo para las imágenes",
   *  "kind": "both",
   *  "beautifulName": "Velo",
   *  "values": "./enums.js:SHADOW_TYPE"
   * }
   */
  shadow: PropTypes.string,
  /**
   * {
   *  "info": "Parametro por si queremos ocultar cards mientras cargan",
   *  "kind": "dev",
   *  "beautifulName": "contentLoaded"
   * }
   */
  contentLoaded: PropTypes.bool,
  /**
   * {
   *  "info": "Campo para el filtrado de la card, se puede poner un único valor o separar por ',' varios valores",
   *  "kind": "both",
   *  "beautifulName": "Categoria"
   * }
   */
  category: PropTypes.string,
  /**
   * {
   *  "info": "Campo para el ordenado de cards",
   *  "kind": "both",
   *  "beautifulName": "Fecha y hora"
   * }
   */
  datetime: PropTypes.string,
  /**
   * {
   *  "info": "Ocultar card",
   *  "kind": "dev",
   *  "beautifulName": "Ocultar card"
   * }
   */
  hide: PropTypes.bool,
};

DetailCard.defaultProps = {
  imageSize: "",
  shadow: "none",
  category: "todos",
  datetime: "",
  hide: false,
};

export default DetailCard;
